import React, { useContext, useEffect, useState } from "react";
import { FieldProps } from "@rjsf/utils";
import {
  get,
  identity,
  isNil,
  keys,
  merge,
  pick,
  set,
  times,
  values,
  zipObject,
} from "lodash";
import { calculateValueFromSquareFootageEstimate } from "common/services/formBuilderService";
import { calculateFullAssessedValue } from "common/utils/improvements";
import { resolveRule } from "common/utils/rules/index";
import type { Rule } from "common/utils/rules/types";
import { formatCurrency } from "common/utils/strings";
import { isNotNil } from "common/utils/tools";
import {
  extractErrorsFromErrorSchema,
  PropertyMarketValueFormData as FormData,
  PropertyMarketValueSchema as FullSchema,
} from "common-client/utils/submissions";
import { FlexColumn } from "../../Common/__styles__/Layout";
import { PrintOnly } from "../../Common/__styles__/PrintOnly";
import { ScreenOnly } from "../../Common/__styles__/ScreenOnly";
import { Body } from "../../Common/Typography";
import { ComplexRadio, Number } from "../../Inputs";
import Wrapper from "../../Inputs/Wrapper";
import { Input } from "../__styles__/Inputs";
import { SubmissionFormContext } from "../Form";
import { getFieldPathFromId, useDefaultedValues } from "../utils";
import {
  Multiplier,
  NumericInputs,
  SummaryBox,
  SummaryRow,
  SummaryText,
} from "./__styles__/PropertyMarketValueField";

const PropertyMarketValue = (
  props: FieldProps<FormData, FullSchema["schema"], FullSchema["uiSchema"]> & {
    isSubmissionBuilder?: boolean;
  },
) => {
  const { onChange, formData, uiSchema, schema, errorSchema } = props;
  const { defaultValues } = useContext(SubmissionFormContext);
  // filter out automatedAssessedValue if not all fields are defaulted,
  // since we can't compute a value then
  const options = schema.oneOf.filter((option, index) => {
    if (option.name === "automatedAssessedValue") {
      return values(uiSchema?.oneOf[index]).every(value => {
        const defaultPath = value["ui:defaultValuePath"];
        return !defaultPath || !isNil(get(defaultValues, defaultPath));
      });
    } else {
      return true;
    }
  });

  const initialOption = options[0]!.name;

  const [initialFormData] = useState(formData ?? { type: initialOption });

  const setSelectedOption = (newSelectedOption: FormData["type"]) => {
    // if the user is editing a form, we need to make sure we don't *lose*
    // the data they had if they switch between the options
    const initialData =
      initialFormData.type === newSelectedOption ? initialFormData : {};

    // force value to `null` if it's not in the initial data so
    // validations apply if you switch between options *before* entering data
    onChange({ value: null, ...initialData, type: newSelectedOption });
  };

  const errors = extractErrorsFromErrorSchema<FormData>(errorSchema);

  return (
    <Input
      isSubmissionBuilder={props.isSubmissionBuilder}
      id={props.idSchema.$id}
    >
      <Wrapper
        name={props.schema.title}
        required={props.required}
        label={props.schema.title}
        compactLabel
        description={props.schema.description}
        tooltip={uiSchema?.["ui:tooltipText"]}
      >
        <div>
          <ScreenOnly width="full">
            {options.map((option, index) => {
              const checked = (formData?.type || initialOption) === option.name;
              const Component = OptionToScreenComponent[
                option.name
              ] as unknown as React.FC<
                SubcomponentProps<
                  | "automatedAssessedValue"
                  | "otherValueEstimate"
                  | "squareFootageEstimate"
                >
              >;

              return (
                <ComplexRadio
                  key={`${props.idSchema.$id}-${option.name}`}
                  value={option.name}
                  name={`${props.idSchema.$id}-${option.name}`}
                  label={option.title}
                  checked={checked}
                  onChange={setSelectedOption}
                  description={option.description}
                  disabled={props.disabled || props.readonly}
                >
                  {checked && (
                    <Component
                      disabled={props.disabled}
                      readonly={props.readonly}
                      {...option}
                      onChange={(newValue: Partial<FormData>) => {
                        const propertiesOnOption = keys(option.properties);

                        // required to mark all fields on the option as dirty
                        // for validation purposes, even if you only edited
                        // one field
                        const emptyDefaults = zipObject(
                          propertiesOnOption,
                          times(propertiesOnOption.length, () => null),
                        );
                        const existingValues = pick(
                          formData,
                          propertiesOnOption,
                        );

                        const finalValue = merge(
                          {},
                          emptyDefaults,
                          existingValues,
                          newValue,
                          {
                            type: option.name,
                          },
                        );

                        onChange(finalValue);
                      }}
                      formData={formData ?? {}}
                      uiSchema={uiSchema!.oneOf[index]}
                      errors={errors}
                      formula={schema.formula}
                      fieldPath={getFieldPathFromId(props.idSchema.$id)}
                    />
                  )}
                </ComplexRadio>
              );
            })}
          </ScreenOnly>
          <PrintOnly
            width="full"
            data-testid={`print-only-${props.schema.title}-${formData?.type}`}
          >
            {formData && (
              <FlexColumn gap="m">
                <FlexColumn gap="xs">
                  <Body size="default" type="emphasis">
                    Estimate type
                  </Body>
                  <Body size="default" type="regular">
                    {options.find(opt => opt.name === formData.type)?.title ||
                      formData.type}
                  </Body>
                </FlexColumn>

                {options.map((option, index) => {
                  if (option.name === formData.type) {
                    const Component = OptionToPrintComponent[
                      option.name
                    ] as unknown as React.FC<
                      SubcomponentProps<
                        | "automatedAssessedValue"
                        | "otherValueEstimate"
                        | "squareFootageEstimate"
                      >
                    >;

                    return (
                      <React.Fragment key={option.name}>
                        <Component
                          onChange={() => {}}
                          {...option}
                          formData={formData}
                          uiSchema={uiSchema!.oneOf[index]}
                          errors={errors}
                        />
                      </React.Fragment>
                    );
                  }

                  return null;
                })}
              </FlexColumn>
            )}
          </PrintOnly>
        </div>
      </Wrapper>
    </Input>
  );
};

type SubcomponentProps<T extends FormData["type"]> = Extract<
  FullSchema["schema"]["oneOf"][number],
  { name: T }
> & {
  onChange: (value: Partial<Extract<FormData, { type: T }>>) => void;
  formData: Omit<Extract<FormData, { type: T }>, "type">;
  uiSchema: Omit<
    Extract<FullSchema["uiSchema"]["oneOf"][number], { _name: T }>,
    "_name"
  >;
  errors?: {
    [key in keyof Omit<Extract<FormData, { type: T }>, "type">]?: Maybe<string>;
  };
  disabled?: boolean;
  readonly?: boolean;
  // This is still used for the existing "other" formulas, but eventually this will be removed
  formula?: Rule;
  fieldPath?: string;
};

const SquareFootageEstimate = (
  props: SubcomponentProps<"squareFootageEstimate">,
) => {
  const { formData, uiSchema, onChange, properties, errors } = props;
  const { sqftOfProperty, costPerSqft, adjustmentRatio, value } = properties;

  useDefaultedValues({
    uiSchema,
    values: formData,
    onChange: onChange,
    //flag ensures we apply the default when the user switches between input types
    skipIfEditing: false,
  });

  const hasValueFormula = (value as { formula?: Rule })?.formula;

  useEffect(() => {
    if (hasValueFormula) return;

    if (
      isNotNil(formData.sqftOfProperty) &&
      isNotNil(formData.costPerSqft) &&
      isNotNil(formData.adjustmentRatio)
    ) {
      const newValue = calculateValueFromSquareFootageEstimate({
        sqftOfProperty: formData.sqftOfProperty,
        costPerSqft: formData.costPerSqft,
        adjustmentRatio: formData.adjustmentRatio,
      });
      if (formData.value !== newValue) {
        onChange({ value: newValue });
      }
    } else if (isNotNil(formData.value)) {
      onChange({ value: null });
    }
  }, [
    hasValueFormula,
    formData.adjustmentRatio,
    formData.costPerSqft,
    formData.sqftOfProperty,
    formData.value,
    onChange,
  ]);

  const fields = ["sqftOfProperty", "costPerSqft", "adjustmentRatio"];
  const hasError = values(pick(errors, fields)).some(identity);

  return (
    <div>
      <NumericInputs>
        <Number
          compactLabel
          title={sqftOfProperty.title}
          value={formData.sqftOfProperty ?? null}
          name={"sqftOfProperty"}
          label={sqftOfProperty.title}
          onChange={sqftOfProperty => onChange({ sqftOfProperty })}
          error={errors?.sqftOfProperty}
          disabled={false}
          readonly={props.readonly || props.disabled}
        />
        <Multiplier hasError={hasError}>X</Multiplier>
        <Number
          compactLabel
          title={costPerSqft.title}
          value={formData.costPerSqft ?? null}
          name={"costPerSqft"}
          label={costPerSqft.title}
          onChange={costPerSqft => onChange({ costPerSqft })}
          error={errors?.costPerSqft}
          disabled={false}
          readonly={props.readonly || props.disabled}
        />
        <Multiplier hasError={hasError}>X</Multiplier>
        <Number
          compactLabel
          title={adjustmentRatio.title}
          value={formData.adjustmentRatio ?? null}
          name={"adjustmentRatio"}
          label={adjustmentRatio.title}
          onChange={adjustmentRatio => onChange({ adjustmentRatio })}
          error={errors?.adjustmentRatio}
          disabled={false}
          readonly={props.readonly || props.disabled}
        />
      </NumericInputs>
      <SummaryBox>
        <SummaryRow>
          <SummaryText>Estimated Value</SummaryText>
          <SummaryText>
            {isNotNil(formData.value) ? formatCurrency(formData.value) : "--"}
          </SummaryText>
        </SummaryRow>
      </SummaryBox>
    </div>
  );
};

const OtherValueEstimate = (props: SubcomponentProps<"otherValueEstimate">) => {
  const { onChange, formData, errors, formula, fieldPath } = props;

  const formulaApplies =
    formula && fieldPath ?
      resolveRule(formula.applies, {
        context: set({}, `formData.${fieldPath}.type`, "otherValueEstimate"),
      })
    : false;

  return (
    <NumericInputs>
      <Number
        compactLabel
        value={formData.value ?? null}
        name={"otherValueEstimate"}
        onChange={value => onChange({ value })}
        prefix={"$"}
        error={errors?.value}
        disabled={false}
        readonly={props.readonly || props.disabled || formulaApplies}
      />
    </NumericInputs>
  );
};

const AutomatedAssessedValue = (
  props: SubcomponentProps<"automatedAssessedValue">,
) => {
  const { formData, uiSchema, onChange, properties } = props;
  const { improvementValue, adjustmentRatio } = useDefaultedValues({
    uiSchema,
    values: formData,
    onChange: onChange,
    skipIfEditing: false,
  });

  const hasValueFormula = (properties.value as { formula?: Rule })?.formula;

  useEffect(() => {
    if (hasValueFormula) return;

    if (!isNil(improvementValue) && !isNil(adjustmentRatio)) {
      const newValue = calculateFullAssessedValue(
        improvementValue,
        adjustmentRatio,
      );
      if (formData.value !== newValue) {
        onChange({
          value: newValue,
        });
      }
    }
  }, [
    hasValueFormula,
    improvementValue,
    adjustmentRatio,
    formData.value,
    onChange,
  ]);

  return (
    <SummaryBox>
      <SummaryRow>
        <SummaryText bold={false}>Improvement Value</SummaryText>
        <SummaryText bold={false}>
          {formatCurrency(formData.improvementValue ?? improvementValue)}
        </SummaryText>
      </SummaryRow>
      <SummaryRow underlined>
        <SummaryText bold={false}>Adjustment Ratio</SummaryText>
        <SummaryText bold={false}>
          {formData.adjustmentRatio ?? adjustmentRatio}
        </SummaryText>
      </SummaryRow>
      <SummaryRow>
        <SummaryText>Assessed Value</SummaryText>
        <SummaryText>{formatCurrency(formData.value)}</SummaryText>
      </SummaryRow>
    </SummaryBox>
  );
};

const OptionToScreenComponent = {
  squareFootageEstimate: SquareFootageEstimate,
  automatedAssessedValue: AutomatedAssessedValue,
  otherValueEstimate: OtherValueEstimate,
};

const SquareFootagePrintComp = ({
  formData,
  properties,
}: SubcomponentProps<"squareFootageEstimate">) => {
  const { sqftOfProperty, costPerSqft, adjustmentRatio } = properties;

  return (
    <>
      <FlexColumn gap="xs">
        <Body size="small" type="emphasis">
          {sqftOfProperty.title}
        </Body>
        <Body size="small" type="regular">
          {formData.sqftOfProperty || "--"}
        </Body>
      </FlexColumn>

      <FlexColumn gap="xs">
        <Body size="small" type="emphasis">
          {costPerSqft.title}
        </Body>
        <Body size="small" type="regular">
          {formData.costPerSqft || "--"}
        </Body>
      </FlexColumn>

      <FlexColumn gap="xs">
        <Body size="small" type="emphasis">
          {adjustmentRatio.title}
        </Body>
        <Body size="small" type="regular">
          {formData.adjustmentRatio || "--"}
        </Body>
      </FlexColumn>

      <FlexColumn gap="xs">
        <Body size="small" type="emphasis">
          Estimated value
        </Body>
        <Body size="small" type="regular">
          {isNotNil(formData.value) ? formatCurrency(formData.value) : "--"}
        </Body>
      </FlexColumn>
    </>
  );
};

const AutomatedValuePrintComp = ({
  formData,
}: SubcomponentProps<"automatedAssessedValue">) => {
  const improvementValue = formData.improvementValue ?? null;
  const adjustmentRatio = formData.adjustmentRatio ?? null;

  return (
    <>
      <FlexColumn gap="xs">
        <Body size="small" type="emphasis">
          Improvement Value
        </Body>
        <Body size="small" type="regular">
          {isNotNil(improvementValue) ? formatCurrency(improvementValue) : "--"}
        </Body>
      </FlexColumn>

      <FlexColumn gap="xs">
        <Body size="small" type="emphasis">
          Adjustment Ratio
        </Body>
        <Body size="small" type="regular">
          {adjustmentRatio || "--"}
        </Body>
      </FlexColumn>

      <FlexColumn gap="xs">
        <Body size="small" type="emphasis">
          Estimated value
        </Body>
        <Body size="small" type="regular">
          {isNotNil(formData.value) ? formatCurrency(formData.value) : "--"}
        </Body>
      </FlexColumn>
    </>
  );
};

const OtherValuePrintComp = ({
  formData,
}: SubcomponentProps<"otherValueEstimate">) => {
  const { value } = formData;

  return (
    <FlexColumn gap="xs">
      <Body size="small" type="emphasis">
        Entered value
      </Body>
      <Body size="default" type="regular">
        {isNotNil(value) ? formatCurrency(value) : "--"}
      </Body>
    </FlexColumn>
  );
};

const OptionToPrintComponent = {
  squareFootageEstimate: SquareFootagePrintComp,
  automatedAssessedValue: AutomatedValuePrintComp,
  otherValueEstimate: OtherValuePrintComp,
};

// get around some weird TS issue with `FieldProps`
export const PropertyMarketValueInput = (props: FieldProps) => {
  return <PropertyMarketValue {...(props as any)} />;
};
